import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK } from "@/theme/tokens";
import { OutreachApproval } from "@/utils/models";
import type { ApprovalView, ApprovalViewKind } from "./outreachApprovalView";

type Props = { view: Extract<ApprovalView, { kind: ApprovalViewKind.Decided }> };

const DONE_TITLE: Record<Exclude<OutreachApproval, OutreachApproval.Waiting>, string> = {
	[OutreachApproval.ByManager]: "request.outreach.approval.done.approved",
	[OutreachApproval.Automatic]: "request.outreach.approval.done.auto_approved",
	[OutreachApproval.NotApproved]: "request.outreach.approval.done.not_approved",
};

/** The latest decision in one sentence: approved or not, then whether we keep looking. */
export const OutreachApprovalDecidedMessage: FC<Props> = ({ view }) => {
	const { t } = useTranslation();
	const { signUp, filled, openSpots } = view;
	if (signUp.approval === OutreachApproval.Waiting) throw new Error(`Outreach: sign-up ${signUp.nurseId} is still waiting, not decided`);
	const approved = signUp.approval !== OutreachApproval.NotApproved;
	const detail = !approved
		? t("request.outreach.approval.done.keep_looking")
		: filled
			? t("request.outreach.approval.done.filled", { name: signUp.name })
			: t("request.outreach.approval.done.open", { name: signUp.name, count: openSpots });
	return (
		<Typography data-testid="outreach-approval-title" sx={{ flex: 1, minWidth: 0, fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY }}>
			<Box component="strong" sx={{ fontWeight: 700 }}>
				{t(DONE_TITLE[signUp.approval])}
			</Box>{" "}
			{detail}
		</Typography>
	);
};
